import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchDashboardData } from '../../actions';
import Overlay from '../Overlay';
import ToastBar from '../ToastBar';
import { SuperAdminAccessControl } from '../AccessControl';
import roles from '../AdminRoles/roles.enum';
import userImg from '../../assets/svg/userDashboard.svg';
import eventImg from '../../assets/svg/eventDashboard.svg';
import channelImg from '../../assets/svg/totalIcons.svg';
import '../../assets/styles/adminBrand.scss';

interface Props { fetchDashboardDetails: any; dashboardData: any; }
const number = (value: any) => Number(value || 0).toLocaleString('en-US');
const percentage = (value: number, total: number) => total > 0 ? value / total * 100 : 0;

function EventRing({ title, paid, free }: { title: string; paid: number; free: number }) {
  const total = paid + free;
  return <div className="avatus-event">
    <div className="avatus-ring" style={{ background: total ? `conic-gradient(var(--av-cyan) 0 ${percentage(paid, total)}%, var(--av-orange) 0 100%)` : 'var(--av-border)' }}>
      <div><strong>{number(total)}</strong><span>{title}</span></div>
    </div>
    <div className="avatus-legend"><span><i />Paid {number(paid)}</span><span><i />Free {number(free)}</span></div>
  </div>;
}

function Ranking({ items, nameKey, countKey, label }: { items: any[]; nameKey: string; countKey: string; label: string }) {
  const max = Math.max(1, ...items.map(item => Number(item[countKey]) || 0));
  return items.length ? <ol className="avatus-ranking" aria-label={label}>
    {items.slice(0, 5).map((item, index) => <li key={item._id || index}>
      <span className="avatus-rank">{String(index + 1).padStart(2, '0')}</span>
      <span className="avatus-rank-name" title={item[nameKey]}>{item[nameKey]}</span>
      <span className="avatus-track" aria-hidden="true"><i style={{ width: `${percentage(Number(item[countKey]) || 0, max)}%` }} /></span>
      <span>{number(item[countKey])}</span>
    </li>)}
  </ol> : <p className="avatus-empty">No records to display.</p>;
}

export const Dashboard = ({ fetchDashboardDetails, dashboardData }: Props) => {
  const [trendingChannelType, setTrendingChannelType] = React.useState('free');
  useEffect(() => { fetchDashboardDetails(trendingChannelType); }, [fetchDashboardDetails, trendingChannelType]);
  if (dashboardData.loadingError) return <ToastBar message={dashboardData.loadingError} handleCloseMethod={() => null} />;
  if (dashboardData.loadingInProgress) return <Overlay />;
  if (!dashboardData.data) return null;
  const data = dashboardData.data;
  const stats = data[2]?.data || {};
  const events = data[3]?.data?.[0] || {};
  const subscriptions = data[4]?.data?.[0] || {};
  const membership = data[5]?.data || {};
  const transactions = data[6]?.data || {};
  const memberships = [
    { label: 'Free', value: membership.free, color: 'var(--av-cyan)' },
    { label: 'Subscription Monthly', value: membership.subscriptioCountMonthly, color: 'var(--av-orange)' },
    { label: 'Subscription Quarterly', value: membership.subscriptioCountQuarterly, color: '#bd9bff' },
    { label: 'Subscription Yearly', value: membership.subscriptioCountYearly, color: '#ffd27a' },
  ];
  return <div className="avatus-dashboard">
    <header className="avatus-page-heading"><p>AVATUS ADMIN</p><h1>Dashboard</h1><div>Your platform, at a glance.</div></header>
    <div className="avatus-metrics">
      {[
        { label: 'Total Users', value: stats.totalUsers, icon: userImg, to: '/usermanagement' },
        { label: 'Total Active Users', value: stats.totalActiveUsers, icon: userImg, to: '/usermanagement?active=true' },
        { label: 'Total Events', value: stats.totalEvents, icon: eventImg, to: '/livestreaming' },
        { label: 'Total Channels', value: stats.totalChannels, icon: channelImg, to: '/channelmanagement' },
      ].map(metric => <Link className="avatus-metric" to={metric.to} key={metric.label}>
        <div>{metric.label}<img src={metric.icon} alt="" /></div><strong>{number(metric.value)}</strong>
      </Link>)}
    </div>
    <div className="avatus-dashboard-grid">
      <section className="avatus-panel"><header><div><h2>Events Overview</h2><p>A clear view of live and upcoming events</p></div></header>
        <div className="avatus-donuts">
          <EventRing title="Live Events" paid={Number(events.eventsLivePaid) || 0} free={Number(events.eventsLiveFree) || 0} />
          <EventRing title="Upcoming Events" paid={Number(events.eventsUpcomingPaid) || 0} free={Number(events.eventsScheduledFree) || 0} />
        </div>
      </section>
      <section className="avatus-panel"><header><div><h2>Top 5 Trending Users</h2><p>Ranked by followers</p></div></header>
        <Ranking items={data[0]?.data || []} nameKey="username" countKey="followersCount" label="User profiles ranked by followers" />
      </section>
      <section className="avatus-panel"><header><div><h2>Top 5 Channels</h2><p>By subscriber count</p></div>
        <label className="avatus-select-label"><span>Category</span><select aria-label="Channel category" value={trendingChannelType} onChange={event => setTrendingChannelType(event.target.value)}><option value="free">Free</option><option value="paid">Paid</option></select></label>
      </header>
        <Ranking items={data[1]?.data || []} nameKey="channelName" countKey="subscriberCount" label="Channels ranked by subscribers" />
        <Link className="avatus-view-all" to="/channelmanagement">View All →</Link>
      </section>
      <section className="avatus-panel"><header><div><h2>Membership Overview</h2><p>Current membership distribution</p></div></header>
        <div className="avatus-member-total"><strong>{number(membership.totalUsers)}</strong><span>Total users</span></div>
        <div className="avatus-stacked" aria-hidden="true">{memberships.map(item => <i key={item.label} style={{ width: `${percentage(Number(item.value) || 0, Number(membership.totalUsers) || 0)}%`, background: item.color }} />)}</div>
        <dl className="avatus-memberships">{memberships.map(item => <div key={item.label}><dt><i style={{ background: item.color }} />{item.label}</dt><dd>{number(item.value)} <small>{percentage(Number(item.value) || 0, Number(membership.totalUsers) || 0).toFixed(2)}%</small></dd></div>)}</dl>
      </section>
      <section className="avatus-panel avatus-wide"><header><div><h2>Subscription Plan Purchase</h2><p>Purchased subscriptions and auto-renewal</p></div></header>
        <div className="avatus-table-scroll"><table className="avatus-subscriptions"><thead><tr><th>Plans</th><th>Purchased</th><th>Auto-Renewal set ON</th></tr></thead><tbody>
          {['Monthly', 'Quarterly', 'Yearly'].map(plan => <tr key={plan}><th>{plan} Plan</th><td>{number(subscriptions[`subscribers${plan}`])}</td><td>{number(subscriptions[`subscribers${plan}Renewal`])}</td></tr>)}
        </tbody></table></div>
      </section>
    </div>
    <SuperAdminAccessControl rules={[roles.superadmin]}>
      <section className="avatus-transactions"><h2>All Transactions Overview</h2><div className="avatus-metrics">
        {[
          { label: 'Total Payment Transactions of Gold AVC (in numbers)', value: transactions.totalCount, unit: '' },
          { label: 'Total Amount of Payment Transactions', value: transactions.total, unit: ' AVC' },
          { label: 'Total Amount of Payment Transactions (Current Month)', value: transactions.monthly, unit: ' AVC' },
        ].map(metric => <div className="avatus-metric" key={metric.label}><div>{metric.label}</div><strong>{number(metric.value)}{metric.unit}</strong></div>)}
      </div></section>
    </SuperAdminAccessControl>
  </div>;
};
const mapStateToProps = (state: any) => ({ dashboardData: state.dashboard });
const mapDispatchToProps = (dispatch: any) => ({ fetchDashboardDetails: (payload: any) => dispatch(fetchDashboardData(payload)) });
export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
